import Button from '@mui/material/Button'
import { PPDialog } from 'components/PPDialog'
import ProfileAvatar from 'components/ProfileAvatar'
import { differenceInMilliseconds } from 'date-fns'
import useConditionalEffect from 'hooks/useConditionalEffect'
import {
  FacebookLoginResponse
} from 'interfaces'
import { useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { NavLink, useNavigate } from 'react-router'
import { mergeMap, Observable, Subject } from 'rxjs'
import { CHECK_TOKENS_INTERVAL_DEFAULT, checkTokens, skipTokensRefresh } from 'services/auth'
import { getLoginStatus } from 'services/auth/identities/facebook'
import { facebookSDKInitializedSelector } from 'services/external/selectors'
import {
  BRAND_YOUTUBE,
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_TIKTOK,
  BRAND_TWITTER
} from 'shared'
import { tokensStateSelector } from 'store/selectors'
import { StoreState, StoreThunkDispatch } from 'store/state'
import styles from './TokensChecker.pcss'

type ExpiredProfile = {
  name: string
  picture: string
}

interface TokensCheckerProps {
  lastCheck?: Date
  expiredFacebookProfiles: Array<{
    name: string
    pageId: string
  }>
  expiredLinkedinProfiles: Array<ExpiredProfile>
  expiredInstagramProfiles: Array<ExpiredProfile>
  expiredPinterestProfiles: Array<ExpiredProfile>
  expiredTwitterProfiles: Array<ExpiredProfile>
  expiredGoogleProfiles: Array<ExpiredProfile>
  expiredTiktokProfiles: Array<ExpiredProfile>
  expiredYTProfiles: Array<ExpiredProfile>
  dialogDismissed: boolean
  fbInitialized: boolean
  checkTokens: () => Observable<any>
  skipTokensRefresh: (dismissDialog?: boolean) => void
  getFBLoginStatus: () => Promise<FacebookLoginResponse>
}

/**
 * TokensChecker component checks fb tokens every {interval} ms
 */
export const TokensChecker = ({
  lastCheck,
  expiredFacebookProfiles,
  expiredLinkedinProfiles,
  expiredInstagramProfiles,
  expiredPinterestProfiles,
  expiredTwitterProfiles,
  expiredGoogleProfiles,
  expiredTiktokProfiles,
  expiredYTProfiles,
  dialogDismissed,
  fbInitialized,
  checkTokens: dispatchCheckTokens,
  skipTokensRefresh,
  getFBLoginStatus
}: TokensCheckerProps) => {
  const navigate = useNavigate()
  const check$ = useRef<Subject<any>>(undefined)
  const timer = useRef<number>(undefined)

  useConditionalEffect(([_getFBLoginStatus, _fbInitialized]) => {
    check$.current = new Subject()
    check$.current.pipe(mergeMap(dispatchCheckTokens)).subscribe()

    const now = new Date()
    const timeSinceLastCheck = differenceInMilliseconds(now, lastCheck || now)

    const handleCheckTokens = () => {
      if (_fbInitialized) {
        _getFBLoginStatus()
      }
      check$.current?.next(null)
    }
    
    if (!lastCheck || Math.abs(timeSinceLastCheck) > CHECK_TOKENS_INTERVAL_DEFAULT) {
      handleCheckTokens()
    }

    timer.current = window.setInterval(handleCheckTokens, CHECK_TOKENS_INTERVAL_DEFAULT)

    return () => {
      window.clearInterval(timer.current)
      check$.current?.unsubscribe()
    }
  }, [], [getFBLoginStatus, fbInitialized])

  const handleGotoSettings = useCallback(() => {
    navigate('/settings/networks')
    skipTokensRefresh(true)
  }, [navigate, skipTokensRefresh])

  const handleSkipRefreshAndDismissDialog = useCallback(() => {
    skipTokensRefresh(true)
  }, [skipTokensRefresh])

  const expiredProfilesCount = expiredFacebookProfiles.length
    + expiredLinkedinProfiles.length
    + expiredInstagramProfiles.length
    + expiredPinterestProfiles.length
    + expiredGoogleProfiles.length
    + expiredTiktokProfiles.length
    + expiredTwitterProfiles.length
    + expiredYTProfiles.length

  const dialogActive = !dialogDismissed && expiredProfilesCount > 0

  return (
    <PPDialog
      open={Boolean(dialogActive)}
      size="sm"
      title="Action Required!"
      onClose={handleSkipRefreshAndDismissDialog}
      footer={
        <div className={styles.actions}>
          <Button
            variant="text"
            onClick={handleSkipRefreshAndDismissDialog}
          >
            <FormattedMessage id="actions.cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGotoSettings}
          >
            Open Settings
          </Button>
        </div>
      }
    >
      <div className={styles.content}>
        <p>
        Your posting tokens for the profiles listed below have been invalidated.
          <br />
        To make sure your posts keep going out to these pages, go to
          <NavLink to="/settings/networks" onClick={handleSkipRefreshAndDismissDialog}> Settings</NavLink>
          {' '}
        and
        click the Refresh/Connect button in the corresponding section.
        </p>
        <div className={styles['profiles-box']}>
          {expiredFacebookProfiles.map(p => {
            const imageUrl = `//graph.facebook.com/${p.pageId}/picture?type=large`
            return (
              <div className={styles['avatar-box']} key={p.pageId}>
                <ProfileAvatar src={imageUrl} brand={BRAND_FACEBOOK} circle title={p.name} />
                <span className={styles['profile-name']}>{p.name}</span>
              </div>
            )
          })}
          {expiredInstagramProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_INSTAGRAM} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredLinkedinProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_LINKEDIN} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredPinterestProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_PINTEREST} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredTwitterProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_TWITTER} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredGoogleProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_GOOGLE} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredTiktokProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_TIKTOK} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
          {expiredYTProfiles.map(p => (
            <div className={styles['avatar-box']} key={p.name}>
              <ProfileAvatar src={p.picture} brand={BRAND_YOUTUBE} circle title={p.name} />
              <span className={styles['profile-name']}>{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </PPDialog>
  )
}

function mapStateToProps(state: StoreState) {
  const tokensState = tokensStateSelector(state)
  const expiredFacebookProfiles = tokensState.refreshRequired?.facebook || []
  const expiredLinkedinProfiles = tokensState.refreshRequired?.linkedin || []
  const expiredInstagramProfiles = tokensState.refreshRequired?.instagram || []
  const expiredPinterestProfiles = tokensState.refreshRequired?.pinterest || []
  const expiredTwitterProfiles = tokensState.refreshRequired?.twitter || []
  const expiredGoogleProfiles = tokensState.refreshRequired?.google || []
  const expiredTiktokProfiles = tokensState.refreshRequired?.tiktok || []
  const expiredYTProfiles = tokensState.refreshRequired?.youtube || []

  return {
    lastCheck: tokensState.lastCheck ? new Date(tokensState.lastCheck) : new Date(),
    expiredFacebookProfiles,
    expiredLinkedinProfiles,
    expiredInstagramProfiles,
    expiredPinterestProfiles,
    expiredTwitterProfiles,
    expiredTiktokProfiles,
    expiredGoogleProfiles,
    expiredYTProfiles,
    dialogDismissed: tokensState.dialogDismissed,
    fbInitialized: facebookSDKInitializedSelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    checkTokens: () => dispatch(checkTokens()),
    skipTokensRefresh: (dismissDialog?: boolean) => dispatch(skipTokensRefresh(dismissDialog)),
    getFBLoginStatus: () => dispatch(getLoginStatus())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TokensChecker)
